import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, ArrowRight, Plus, FileText, Users, Package, ShoppingCart, Building2, Calendar, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ERPNext-style awesome bar. Type to filter; arrow keys to navigate; Enter to go.
// Supports:
//   - Page jump:  "inv"      → /inventory
//   - New record: "new sales order"  → /sales?action=new
//   - Search:     "John"     → calls /api/global-search

const PAGES = [
  { label: 'Home', path: '/', icon: BarChart3, keywords: 'home dashboard' },
  { label: 'Inventory', path: '/inventory', icon: Package, keywords: 'stock items' },
  { label: 'Sales', path: '/sales', icon: ShoppingCart, keywords: 'sales orders' },
  { label: 'POS', path: '/pos', icon: ShoppingCart, keywords: 'point of sale' },
  { label: 'Customers', path: '/customers', icon: Users, keywords: 'crm clients' },
  { label: 'Suppliers', path: '/suppliers', icon: Building2, keywords: 'vendors' },
  { label: 'Purchase Orders', path: '/purchase-orders', icon: FileText, keywords: 'po purchases' },
  { label: 'Stock Transfers', path: '/stock-transfers', icon: Package, keywords: 'transfer warehouse' },
  { label: 'Employees', path: '/hr/employees', icon: Users, keywords: 'staff hr' },
  { label: 'Leave', path: '/hr/leave', icon: Calendar, keywords: 'time off vacation' },
  { label: 'Attendance', path: '/hr/attendance', icon: Calendar, keywords: 'clock in' },
  { label: 'Payroll', path: '/hr/payroll', icon: FileText, keywords: 'salary wages' },
  { label: 'Accounting', path: '/finance', icon: FileText, keywords: 'finance gl ledger' },
  { label: 'Invoices', path: '/finance/invoices', icon: FileText, keywords: 'ar receivables' },
  { label: 'Bills', path: '/finance/bills', icon: FileText, keywords: 'ap payables' },
  { label: 'Expenses', path: '/finance/expenses', icon: FileText, keywords: 'spending' },
  { label: 'Reports', path: '/reports', icon: BarChart3, keywords: 'analytics' },
  { label: 'Branches', path: '/branches', icon: Building2, keywords: 'locations' },
  { label: 'Notifications', path: '/notifications', icon: Calendar, keywords: 'alerts inbox' },
  { label: 'Settings', path: '/settings', icon: FileText, keywords: 'configuration' },
];

export default function AwesomeBar() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const results = useMemo(() => {
    const text = q.trim().toLowerCase();
    if (!text) return PAGES.slice(0, 8).map(p => ({ ...p, kind: 'page' }));

    const out = [];
    // "new <thing>" → action shortcut
    if (text.startsWith('new ')) {
      const target = text.slice(4);
      PAGES.forEach(p => {
        if (p.label.toLowerCase().includes(target)) {
          out.push({ ...p, label: `+ New ${p.label.replace(/s$/, '')}`, kind: 'action', path: `${p.path}?action=new` });
        }
      });
    }
    PAGES.forEach(p => {
      const hay = (p.label + ' ' + p.keywords).toLowerCase();
      if (hay.includes(text)) out.push({ ...p, kind: 'page' });
    });
    return out.slice(0, 12);
  }, [q]);

  // Global keyboard shortcut: Ctrl/Cmd+G or Ctrl/Cmd+K
  useEffect(() => {
    const onKey = (e) => {
      const isMeta = e.ctrlKey || e.metaKey;
      if (isMeta && (e.key === 'g' || e.key === 'k' || e.key === '/')) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
      if (e.key === 'Escape' && open) { setOpen(false); inputRef.current?.blur(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Outside-click close
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const choose = (item) => {
    setOpen(false);
    setQ('');
    navigate(item.path);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[activeIdx]) { e.preventDefault(); choose(results[activeIdx]); }
  };

  return (
    <div className="erp-awesome-bar" ref={containerRef}>
      <Search className="erp-search-icon" size={14} />
      <input
        ref={inputRef}
        type="search"
        placeholder="Search or jump to… (Ctrl+G)"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActiveIdx(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        aria-label="Awesome bar"
        aria-haspopup="listbox"
        aria-expanded={open}
      />
      <span className="erp-shortcut-hint">Ctrl+G</span>
      {open && results.length > 0 && (
        <div className="erp-awesome-results" role="listbox">
          {results.map((r, i) => {
            const Icon = r.icon || ArrowRight;
            return (
              <div
                key={`${r.kind}-${r.path}-${i}`}
                className={`erp-awesome-result${i === activeIdx ? ' active' : ''}`}
                role="option"
                aria-selected={i === activeIdx}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => choose(r)}
              >
                {r.kind === 'action' ? <Plus className="icon" size={14} /> : <Icon className="icon" size={14} />}
                <span className="label">{r.label}</span>
                <span className="desc">{r.kind === 'action' ? 'Create' : 'Page'}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
